import { useState } from "react";
import LureoButton from '@/common/LureoButton';
import { Input } from "@/components/cms/ui/input";
import { FieldLabel } from "@/components/ui/text";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/cms/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/components/cms/ui/sonner";
import { LoadingSpinner } from "@/common/LoadingSpinner";

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LoginDialog({ open, onOpenChange }: LoginDialogProps) {
  const { signIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(formData.email, formData.password);
      toast.success("Welcome back!", { description: "You have successfully signed in." });
      onOpenChange(false);
      setFormData({ email: "", password: "" });
    } catch (error: any) {
      toast.error("Sign in failed", { description: error.message || "Failed to sign in" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Sign In</DialogTitle>
          <DialogDescription>
            Sign in to your account to get started.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSignIn} className="flex min-h-0 flex-1 flex-col">
          <DialogBody>
            <div className="space-y-2">
              <FieldLabel variant="plain" htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="space-y-2">
              <FieldLabel variant="plain" htmlFor="password">Password</FieldLabel>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter your password"
                required
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <LureoButton type="submit" variant="secondary" suppressTooltip className="h-9 px-4 rounded-lureo-radius-button-pill" disabled={loading}>
              {loading && <LoadingSpinner className="h-4 w-4" />}
              Sign In
            </LureoButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}